import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { uploadFile, openFile, downloadFile, AuthThumb } from "../lib/uploads";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, ExternalLink } from "lucide-react";

export default function Documents() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [progress, setProgress] = useState(null);
  const ref = useRef();
  const load = () => api.get("/documents").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setProgress(0);
    try { await uploadFile(file, { entity_type: "document" }, (p) => setProgress(Math.round(p * 100))); toast.success(t("save")); load(); }
    catch { toast.error(t("upload_failed")); }
    finally { setProgress(null); if (ref.current) ref.current.value = ""; }
  };
  const open = async (f) => {
    try { await openFile(f); }
    catch { toast.error(t("error")); }
  };
  const download = async (f) => {
    try { await downloadFile(f); }
    catch { toast.error(t("error")); }
  };
  const del = async (id) => {
    if (!window.confirm(t("delete_doc_confirm"))) return;
    try { await api.delete(`/files/${id}`); toast.success(t("deleted")); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const types = [...new Set(rows.map((r) => r.entity_type))];
  const list = rows.filter((r) => !filter || r.entity_type === filter);

  return (
    <div data-testid="documents-page">
      <PageHeader title={t("documents")} subtitle={`${rows.length}`}
        action={can("documents.manage") && <>
          <input ref={ref} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="doc-input" />
          <Button onClick={() => ref.current.click()} disabled={progress !== null} className="bg-[var(--amber)] text-white gap-2" data-testid="doc-upload-btn"><Upload size={16} /> {progress === null ? t("upload") : `${progress}%`}</Button>
        </>} />
      <div className="flex gap-2 mb-3 flex-wrap">
        {["", ...types].map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`doc-filter-${s || "all"}`} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === s ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{s ? t(s) : t("total")}</button>)}
      </div>
      {list.length === 0 ? <Empty /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((f) => (
            <div key={f.id} className="tactical-card p-3 flex items-center gap-3" data-testid={`doc-${f.id}`}>
              {f.content_type?.startsWith("image/") ? <AuthThumb rec={f} className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded bg-[var(--bg)] flex items-center justify-center">{f.content_type?.startsWith("image/") ? <ImageIcon /> : <FileText className="text-[var(--amber)]" />}</div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{f.original_filename}</div>
                <div className="text-xs text-[var(--text-3)]">{t(f.entity_type)} · {Math.round((f.size || 0) / 1024)} KB · {new Date(f.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => open(f)} className="p-2" title={t("inv_open")} data-testid={`doc-open-${f.id}`}><ExternalLink size={16} /></button>
              <button onClick={() => download(f)} className="p-2" title={t("download")} data-testid={`doc-download-${f.id}`}><Download size={16} /></button>
              {can("documents.manage") && <button onClick={() => del(f.id)} className="p-2 text-[var(--red)]" data-testid={`doc-del-${f.id}`}><Trash2 size={16} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
